/**
 * Double Door customization sheet (pairs).
 *
 * Companion to snippets/double-door-configurator.liquid on the
 * product.double-door template. Speaks the exact same cart protocol as
 * assets/door-configurator.js (the single-door builder) so
 * door-addon-cart-sync.js and the receipt emails group these orders with
 * zero changes: one main line (the pair) + hidden "Door Builder - " add-on
 * lines under a shared _Door Config ID.
 *
 * Pair-aware quantities: the base product IS the pair, so quantity is in
 * pairs. Machining and frame add-ons ride at 1x the pair quantity; the
 * hinge add-on (a per-door set) rides at 2x via _Door Add-On Multiplier.
 */
(function () {
  "use strict";

  var FIELD_KEYS = {
    mach_type: ["mach_type"],
    mach_custom_measurements: [
      "top_hinge_top",
      "middle_hinge_top",
      "bottom_hinge_top"
    ],
    mach_hinges: ["mach_hinges"],
    mach_hinge_finish: ["mach_hinge_finish"],
    frame_type: ["frame_type"],
    jamb_size: ["jamb_size"],
    frame_hinges: ["frame_hinges"],
    frame_hinge_finish: ["frame_hinge_finish"]
  };

  var PATH_STEP_KEY_ORDER = {
    machining: [
      "mach_type",
      "top_hinge_top",
      "middle_hinge_top",
      "bottom_hinge_top",
      "mach_hinges",
      "mach_hinge_finish"
    ],
    frame_set: [
      "frame_type",
      "jamb_size",
      "frame_hinges",
      "frame_hinge_finish"
    ]
  };

  // Double doors are fitted with dummy handles (surface mounted), so a pair is
  // never bored for a handle. Stated on every order line for the shop.
  var NO_LOCK_PREP = "None (dummy handles, no hole)";

  var STATE_KEY_TO_FIELD = (function () {
    var mapping = {};
    Object.keys(FIELD_KEYS).forEach(function (fieldName) {
      FIELD_KEYS[fieldName].forEach(function (key) {
        if (!mapping[key]) mapping[key] = fieldName;
      });
    });
    return mapping;
  })();

  var INTERNAL_PROPERTY_BLOCKLIST = {
    "_Door Main Item": true,
    "_Door Main Multiplier": true,
    "_Door Add-On": true,
    "_Door Add-On Label": true,
    "_Door Add-On Key": true,
    "_Door Add-On Multiplier": true,
    "_Door Config ID": true,
    "_door_id": true,
    "_parent_door_id": true
  };

  function asMoney(cents) {
    if (
      window.theme &&
      typeof window.theme.formatMoney === "function" &&
      window.theme.moneyFormat
    ) {
      if (
        window.theme.settings &&
        window.theme.settings.currency_code_enable &&
        window.theme.moneyWithCurrencyFormat
      ) {
        return window.theme.formatMoney(cents, window.theme.moneyWithCurrencyFormat);
      }
      return window.theme.formatMoney(cents, window.theme.moneyFormat);
    }
    return "$" + (cents / 100).toFixed(2);
  }

  function parseNumber(value, fallback) {
    var parsed = parseInt(value, 10);
    if (!Number.isFinite(parsed)) return fallback;
    return parsed;
  }

  function parseDimension(rawValue) {
    var raw = String(rawValue || "")
      .replace(/\b(inches|inch|in)\b/gi, "")
      .replace(/[^0-9./\-\s]/g, "")
      .trim()
      .replace(/\s+/g, " ");

    if (!raw) return { valid: false, decimal: null };

    if (/^\d+(\.\d+)?$/.test(raw)) {
      return { valid: true, decimal: parseFloat(raw) };
    }

    var mixed = raw.match(/^(\d+)\s*[- ]\s*(\d+)\s*\/\s*(\d+)$/);
    if (mixed) {
      var whole = parseInt(mixed[1], 10);
      var numerator = parseInt(mixed[2], 10);
      var denominator = parseInt(mixed[3], 10);
      if (!denominator) return { valid: false, decimal: null };
      return { valid: true, decimal: whole + numerator / denominator };
    }

    return { valid: false, decimal: null };
  }

  function formatHingeMark(raw) {
    var v = String(raw == null ? "" : raw).replace(/["']/g, "").trim();
    return v + '"';
  }

  function DoubleDoorSheet(container) {
    this.container = container;
    this.productInfo = container.closest("product-info");
    this.formId = container.getAttribute("data-product-form-id") || "";
    this.form = document.getElementById(this.formId);
    this.unavailable = container.hasAttribute("data-sheet-unavailable");

    if (!this.form && this.productInfo) {
      this.form = this.productInfo.querySelector("form[action*='/cart/add']");
    }
    if (!this.form) return;

    this.submitButton = this.form.querySelector('button[type="submit"][name="add"]');
    this.addonProducts = this.readJson("[data-addon-products]") || {};
    this.baseVariantPrices = this.readJson("[data-base-variant-prices]") || {};

    this.pricingList = container.querySelector("[data-addon-pricing-list]");
    this.pricingTotal = container.querySelector("[data-addon-pricing-total]");
    this.pricingEmpty = container.querySelector("[data-addon-pricing-empty]");
    this.submitError = container.querySelector("[data-submit-error]");
    this.expandedContent = container.querySelector("[data-expanded-content]");

    this.state = { path: "" };

    this.clearVariantPreselection();
    this.bindEvents();
    this.render();
    this.watchSubmitPrice();
  }

  // Nobody should be able to add a pair without consciously picking a size, so
  // start with no size selected (the theme preselects the first one). custom.js
  // does the same for the single builder; repeated here so the sheet does not
  // depend on that shared file.
  DoubleDoorSheet.prototype.clearVariantPreselection = function () {
    var scope = this.productInfo || this.form;
    if (!scope) return;

    var inputs = scope.querySelectorAll(
      ".radio__input[data-single-option-selector], .swatch__input[data-single-option-selector]"
    );
    if (!inputs.length) return;

    inputs.forEach(function (input) {
      input.checked = false;
      input.removeAttribute("checked");
    });

    var variantIdInput = this.form.querySelector("input[name='id']");
    if (variantIdInput && variantIdInput.value) {
      variantIdInput.value = "";
      variantIdInput.dispatchEvent(new Event("change", { bubbles: true }));
    }
  };

  DoubleDoorSheet.prototype.getSelectedVariantId = function () {
    var variantInput = this.form.querySelector("input[name='id']");
    return variantInput ? parseNumber(variantInput.value, 0) : 0;
  };

  DoubleDoorSheet.prototype.hasSizeOptions = function () {
    var scope = this.productInfo || this.form;
    return Boolean(
      scope &&
        scope.querySelector(
          ".radio__input[data-single-option-selector], .swatch__input[data-single-option-selector]"
        )
    );
  };

  DoubleDoorSheet.prototype.readJson = function (selector) {
    var node = this.container.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  DoubleDoorSheet.prototype.bindEvents = function () {
    var self = this;

    this.container.addEventListener("click", function (event) {
      var pathButton = event.target.closest("[data-path-option]");
      if (pathButton && self.container.contains(pathButton)) {
        self.selectPath(pathButton.value);
        return;
      }

      var resetButton = event.target.closest("[data-path-reset]");
      if (resetButton && self.container.contains(resetButton)) {
        self.resetAll();
        return;
      }

      var choiceButton = event.target.closest("[data-choice]");
      if (choiceButton && self.container.contains(choiceButton)) {
        var control = choiceButton.closest("[data-control]");
        if (control) {
          self.setChoice(control.getAttribute("data-control"), choiceButton.value);
        }
      }
    });

    this.container.addEventListener("input", function (event) {
      var input = event.target.closest("[data-input]");
      if (!input) return;
      var key = input.getAttribute("data-input");
      self.state[key] = input.value.trim();
      self.hideError(key);
      self.hideSubmitError();
    });

    var photoInput = this.getHingePhotoInput();
    if (photoInput) {
      photoInput.addEventListener("change", function () {
        self.handleHingePhotoChange(photoInput);
      });
    }

    this.form.addEventListener(
      "change",
      function (event) {
        var target = event.target;
        if (!target || !target.matches) return;
        if (target.matches("[name='quantity'], [data-quantity-field]")) {
          self.updatePricing();
          return;
        }
        // Size choice changes the variant the button price is based on, and
        // clears a pending "pick your size" error.
        if (target.matches("[data-single-option-selector], input[name='id']")) {
          self.hideSubmitError();
          self.updatePricing();
        }
      }
    );

    this.bindQuantityListeners();

    this.form.addEventListener(
      "submit",
      function (event) {
        // A route is required on the pair sheet — there is no blank-pair
        // fall-through, so the native submit never runs.
        event.preventDefault();
        event.stopImmediatePropagation();
        self.hideSubmitError();

        if (self.unavailable) {
          self.showSubmitError(
            "Door options are temporarily unavailable online. Please contact us to order this pair."
          );
          return;
        }

        if (self.hasSizeOptions() && !self.getSelectedVariantId()) {
          self.showSubmitError("Please pick your door size above. The size is for one door.");
          self.scrollToSizePicker();
          return;
        }

        if (!self.state.path) {
          self.showSubmitError(
            "Please pick one of the two options above so we know how to prepare your doors."
          );
          self.scrollToSheet();
          return;
        }

        var validation = self.validate();
        if (!validation.valid) {
          self.showSubmitError(validation.message);
          return;
        }

        var payload = self.buildCartPayload(validation.properties);
        if (!payload.valid) {
          self.showSubmitError(payload.message);
          return;
        }

        if (payload.hingePhotoFile) {
          self.submitCartPayloadWithPhoto(payload.items, payload.hingePhotoFile);
        } else {
          self.submitCartPayload(payload.items);
        }
      },
      true
    );
  };

  DoubleDoorSheet.prototype.bindQuantityListeners = function () {
    var self = this;
    var scope = this.productInfo || this.form;
    var inputs = scope.querySelectorAll("[name='quantity'], [data-quantity-field]");
    inputs.forEach(function (input) {
      if (input.__doubleDoorBound) return;
      input.__doubleDoorBound = true;
      input.addEventListener("change", function () {
        self.updatePricing();
      });
      input.addEventListener("input", function () {
        self.updatePricing();
      });
    });
  };

  DoubleDoorSheet.prototype.scrollToSheet = function () {
    if (this.container.scrollIntoView) {
      this.container.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  DoubleDoorSheet.prototype.scrollToSizePicker = function () {
    var scope = this.productInfo || this.form;
    var input = scope
      ? scope.querySelector(
          ".radio__input[data-single-option-selector], .swatch__input[data-single-option-selector]"
        )
      : null;
    var target = input ? input.closest("[data-selector-wrapper]") || input.parentElement : null;
    if (target && target.scrollIntoView) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  DoubleDoorSheet.prototype.selectPath = function (path) {
    if (!path || this.state.path === path) return;
    this.clearAllValues();
    this.state.path = path;
    this.hideSubmitError();
    this.render();
  };

  DoubleDoorSheet.prototype.resetAll = function () {
    this.clearAllValues();
    this.clearHingePhoto();
    this.hideSubmitError();
    this.render();
  };

  DoubleDoorSheet.prototype.clearAllValues = function () {
    var self = this;
    this.state = { path: "" };
    this.container.querySelectorAll("[data-input]").forEach(function (input) {
      input.value = "";
    });
    Object.keys(STATE_KEY_TO_FIELD).forEach(function (key) {
      self.hideError(key);
    });
  };

  DoubleDoorSheet.prototype.setChoice = function (controlKey, value) {
    if (!controlKey) return;
    if (this.state[controlKey] === value) return;

    this.state[controlKey] = value;
    this.clearDownstream(controlKey);
    this.hideSubmitError();
    this.render();
  };

  // Changing an upstream answer clears everything after it so the sheet can
  // never carry stale downstream choices (same rule as the single builder).
  DoubleDoorSheet.prototype.clearDownstream = function (controlKey) {
    var order = PATH_STEP_KEY_ORDER[this.state.path] || [];
    var index = order.indexOf(controlKey);
    if (index === -1) return;

    var self = this;
    order.slice(index + 1).forEach(function (key) {
      delete self.state[key];
      var input = self.container.querySelector("[data-input='" + key + "']");
      if (input) input.value = "";
      self.hideError(key);
    });

    if (this.state.path === "machining" && controlKey === "mach_type") {
      this.clearHingePhoto();
    }
  };

  // ----- Rendering -----

  DoubleDoorSheet.prototype.render = function () {
    var state = this.state;
    var container = this.container;

    container.querySelectorAll("[data-path-option]").forEach(function (button) {
      var selected = state.path === button.value;
      button.classList.toggle("is-selected", selected);
      button.setAttribute("aria-pressed", selected ? "true" : "false");
    });

    var resetButton = container.querySelector("[data-path-reset]");
    if (resetButton) resetButton.classList.toggle("is-hidden", !state.path);

    if (this.expandedContent) {
      // door-configurator.css enumerates its .is-hidden selectors; this
      // element is not among them, so toggle display directly.
      this.expandedContent.style.display = state.path ? "" : "none";
    }

    container.querySelectorAll("[data-workflow]").forEach(function (workflow) {
      workflow.classList.toggle(
        "is-hidden",
        workflow.getAttribute("data-workflow") !== state.path
      );
    });

    var visibleFields = {};
    if (state.path === "machining") {
      visibleFields.mach_type = true;
      if (state.mach_type === "custom") visibleFields.mach_custom_measurements = true;
      if (state.mach_type) visibleFields.mach_hinges = true;
      if (state.mach_hinges === "yes") visibleFields.mach_hinge_finish = true;
    }
    if (state.path === "frame_set") {
      visibleFields.frame_type = true;
      if (state.frame_type) visibleFields.jamb_size = true;
      if (state.jamb_size) visibleFields.frame_hinges = true;
      if (state.frame_hinges === "yes") visibleFields.frame_hinge_finish = true;
    }

    container.querySelectorAll("[data-field]").forEach(function (field) {
      field.classList.toggle(
        "is-hidden",
        !visibleFields[field.getAttribute("data-field")]
      );
    });

    container.querySelectorAll("[data-control]").forEach(function (control) {
      var key = control.getAttribute("data-control");
      control.querySelectorAll("[data-choice]").forEach(function (button) {
        var selected = state[key] === button.value;
        button.classList.toggle("is-selected", selected);
        button.setAttribute("aria-pressed", selected ? "true" : "false");
      });
    });

    this.updatePricing();
    this.updateOutcome();
  };

  // ----- Add-on selection + pricing -----

  DoubleDoorSheet.prototype.collectAddonKeys = function () {
    var state = this.state;
    var keys = [];

    if (state.path === "machining") {
      if (state.mach_type === "standard") keys.push("mach_standard");
      if (state.mach_type === "custom") keys.push("mach_custom");
      if (state.mach_hinges === "yes") {
        if (state.mach_hinge_finish === "black") keys.push("hinges_black");
        if (state.mach_hinge_finish === "satin_nickel") keys.push("hinges_satin_nickel");
      }
    }

    if (state.path === "frame_set") {
      if (state.frame_type && state.jamb_size) {
        if (state.frame_type === "mdf" && state.jamb_size === "4_5_8") keys.push("frame_mdf_4_58");
        if (state.frame_type === "mdf" && state.jamb_size === "6_5_8") keys.push("frame_mdf_6_58");
        if (state.frame_type === "fj_pine" && state.jamb_size === "4_5_8") keys.push("frame_fj_pine_4_58");
        if (state.frame_type === "fj_pine" && state.jamb_size === "6_5_8") keys.push("frame_fj_pine_6_58");
      }
      // A new frame always ships with both doors machined to standard.
      keys.push("mach_standard");
      if (state.frame_hinges === "yes") {
        if (state.frame_hinge_finish === "black") keys.push("hinges_black");
        if (state.frame_hinge_finish === "satin_nickel") keys.push("hinges_satin_nickel");
      }
    }

    return keys;
  };

  DoubleDoorSheet.prototype.getQuantity = function () {
    var qtyInput = this.productInfo
      ? this.productInfo.querySelector("[data-quantity-field]")
      : null;
    if (qtyInput) return Math.max(parseNumber(qtyInput.value, 1), 1);

    var formQty = this.form.querySelector("[name='quantity']");
    if (formQty) return Math.max(parseNumber(formQty.value, 1), 1);

    return 1;
  };

  DoubleDoorSheet.prototype.getAddonMultiplier = function (addonKey) {
    var addon = this.addonProducts[addonKey];
    return Math.max(parseNumber(addon && addon.multiplier, 1), 1);
  };

  DoubleDoorSheet.prototype.updatePricing = function () {
    if (!this.pricingList || !this.pricingTotal || !this.pricingEmpty) return;

    this.bindQuantityListeners();

    var self = this;
    var quantity = this.getQuantity();
    var keys = this.collectAddonKeys();
    var perPairTotal = 0;

    this.pricingList.innerHTML = "";

    keys.forEach(function (key) {
      var addon = self.addonProducts[key];
      if (!addon) return;
      var multiplier = self.getAddonMultiplier(key);
      var amount = addon.price * multiplier;
      perPairTotal += amount;

      var row = document.createElement("li");
      row.className = "door-addon-configurator__pricing-row";
      var label = document.createElement("span");
      label.textContent = addon.label;
      var price = document.createElement("span");
      price.textContent = "+" + asMoney(amount);
      row.appendChild(label);
      row.appendChild(price);
      self.pricingList.appendChild(row);
    });

    // door-configurator.css enumerates its .is-hidden selectors; these two
    // elements are not among them, so toggle display directly.
    this.pricingEmpty.style.display = keys.length ? "none" : "";
    this.pricingList.style.display = keys.length ? "" : "none";

    var total = perPairTotal * quantity;
    this.pricingTotal.textContent =
      asMoney(total) + (quantity > 1 ? " for " + quantity + " pairs" : "");

    this.applyButtonPrice();
  };

  // What the Add to Cart button should read: (pair price + options) x quantity.
  // Always derived from live DOM so it stays right after the theme swaps the
  // variant. Returns null when no size is chosen yet — then the theme's own
  // price stands.
  DoubleDoorSheet.prototype.computeDesiredButtonPrice = function () {
    var variantInput = this.form.querySelector("[name='id']");
    var basePrice = variantInput
      ? parseNumber(this.baseVariantPrices[String(variantInput.value)], 0)
      : 0;
    if (!basePrice) return null;

    var self = this;
    var perPairTotal = this.collectAddonKeys().reduce(function (sum, key) {
      var addon = self.addonProducts[key];
      if (!addon) return sum;
      return sum + addon.price * self.getAddonMultiplier(key);
    }, 0);

    return asMoney((basePrice + perPairTotal) * this.getQuantity());
  };

  DoubleDoorSheet.prototype.applyButtonPrice = function () {
    if (this.applyingPrice) return;

    if (!this.submitButton || !document.body.contains(this.submitButton)) {
      this.submitButton = this.form.querySelector('button[type="submit"][name="add"]');
    }
    if (!this.submitButton) return;

    var submitPrice = this.submitButton.querySelector("[data-product-price]");
    if (!submitPrice) return;

    var desired = this.computeDesiredButtonPrice();
    if (desired === null) return;
    if (submitPrice.textContent.trim() === desired) return;

    this.applyingPrice = true;
    submitPrice.textContent = desired;
    submitPrice.classList.remove("hidden");

    var submitComparePrice = this.submitButton.querySelector("[data-compare-price]");
    if (submitComparePrice) {
      submitComparePrice.classList.add("hidden");
      submitComparePrice.style.display = "none";
    }
    this.applyingPrice = false;
  };

  // Picking a size makes the theme re-render the price/buy-button area a moment
  // later, wiping the options out of the button price. Re-assert it whenever
  // that area changes, so the button can never understate what will be charged.
  DoubleDoorSheet.prototype.watchSubmitPrice = function () {
    if (this.priceObserver || typeof MutationObserver === "undefined") return;
    var scope = this.productInfo || this.form;
    if (!scope) return;

    var self = this;
    this.priceObserver = new MutationObserver(function () {
      self.applyButtonPrice();
    });
    this.priceObserver.observe(scope, {
      childList: true,
      subtree: true,
      characterData: true
    });
  };

  DoubleDoorSheet.prototype.updateOutcome = function () {
    var state = this.state;
    var self = this;

    function setRow(rowKey, done, status, amountCents) {
      var row = self.container.querySelector("[data-outcome-row='" + rowKey + "']");
      if (!row) return;
      row.classList.toggle("is-done", Boolean(done));
      var statusEl = row.querySelector("[data-outcome-status]");
      if (statusEl) statusEl.textContent = status;
      var amountEl = row.querySelector("[data-outcome-amount]");
      if (amountEl) {
        amountEl.textContent = amountCents ? "+" + asMoney(amountCents) : "";
      }
    }

    var machStandard = this.addonProducts.mach_standard || { price: 0 };
    var machCustom = this.addonProducts.mach_custom || { price: 0 };
    var hinges = this.addonProducts.hinges_black || { price: 0, multiplier: 2 };
    var hingesAmount = hinges.price * Math.max(parseNumber(hinges.multiplier, 1), 1);

    var pockets = { done: false, status: "not cut", amount: 0 };
    var hingesRow = { done: false, status: "not included", amount: 0 };
    var frameRow = { done: false, status: "not included", amount: 0 };

    if (state.path === "machining") {
      frameRow = { done: true, status: "you keep yours", amount: 0 };
      if (state.mach_type === "standard") {
        pockets = { done: true, status: "cut at 8 in and 69 in", amount: machStandard.price };
      }
      if (state.mach_type === "custom") {
        pockets = { done: true, status: "cut to match your doors", amount: machCustom.price };
      }
      if (state.mach_hinges === "yes") {
        hingesRow = { done: true, status: "4 included", amount: hingesAmount };
      }
    }

    if (state.path === "frame_set") {
      pockets = { done: true, status: "cut at 8 in and 69 in", amount: machStandard.price };
      var frameKey = null;
      if (state.frame_type && state.jamb_size) {
        frameKey =
          (state.frame_type === "mdf" ? "frame_mdf_" : "frame_fj_pine_") +
          (state.jamb_size === "4_5_8" ? "4_58" : "6_58");
      }
      if (frameKey && this.addonProducts[frameKey]) {
        frameRow = { done: true, status: "included", amount: this.addonProducts[frameKey].price };
      } else {
        frameRow = { done: false, status: "pick material + depth", amount: 0 };
      }
      if (state.frame_hinges === "yes") {
        hingesRow = { done: true, status: "4 included", amount: hingesAmount };
      }
    }

    setRow("hinge_pockets", pockets.done, pockets.status, pockets.amount);
    setRow("hinges", hingesRow.done, hingesRow.status, hingesRow.amount);
    setRow("frame", frameRow.done, frameRow.status, frameRow.amount);
  };

  // ----- Errors -----

  DoubleDoorSheet.prototype.showError = function (key, message) {
    var el = this.container.querySelector("[data-error-for='" + key + "']");
    if (!el) return;
    el.textContent = message;
    el.classList.remove("is-hidden");
  };

  DoubleDoorSheet.prototype.hideError = function (key) {
    var el = this.container.querySelector("[data-error-for='" + key + "']");
    if (!el) return;
    el.textContent = "";
    el.classList.add("is-hidden");
  };

  DoubleDoorSheet.prototype.showSubmitError = function (message) {
    if (!this.submitError) return;
    this.submitError.textContent = message;
    this.submitError.classList.remove("is-hidden");
  };

  DoubleDoorSheet.prototype.hideSubmitError = function () {
    if (!this.submitError) return;
    this.submitError.textContent = "";
    this.submitError.classList.add("is-hidden");
  };

  // ----- Measurement validation (same rules as the single builder) -----

  DoubleDoorSheet.prototype.validateHingeMark = function (key, required, min, max) {
    var value = this.state[key];

    if (!value && !required) {
      this.hideError(key);
      return { valid: true, empty: true };
    }
    if (!value && required) {
      this.showError(key, "This measurement is required.");
      return { valid: false, empty: true };
    }

    var parsed = parseDimension(value);
    if (!parsed.valid) {
      this.showError(key, "Use a valid format like 7, 7.25, 7-1/4, or 7 1/4.");
      return { valid: false, empty: false };
    }
    if (parsed.decimal < min || parsed.decimal > max) {
      this.showError(key, "Enter a measurement between " + min + " and " + max + " inches.");
      return { valid: false, empty: false };
    }

    this.hideError(key);
    return { valid: true, empty: false };
  };

  // ----- Hinge photo (required on custom machining) -----

  DoubleDoorSheet.prototype.getHingePhotoInput = function () {
    return this.container.querySelector("[data-hinge-photo-input]");
  };

  DoubleDoorSheet.prototype.hasHingePhoto = function () {
    var input = this.getHingePhotoInput();
    return Boolean(input && input.files && input.files.length > 0);
  };

  DoubleDoorSheet.prototype.handleHingePhotoChange = function (input) {
    var nameEl = this.container.querySelector("[data-hinge-photo-name]");
    var previewEl = this.container.querySelector("[data-hinge-photo-preview]");
    var file = input.files && input.files[0];

    function clearMeta() {
      if (nameEl) {
        nameEl.classList.add("is-hidden");
        nameEl.textContent = "";
      }
      if (previewEl) {
        previewEl.classList.add("is-hidden");
        previewEl.removeAttribute("src");
      }
    }

    if (!file) {
      this.clearHingePhoto();
      return;
    }

    var nameLower = (file.name || "").toLowerCase();
    var isImage =
      /^image\//.test(file.type || "") ||
      /\.(jpe?g|png|gif|webp|heic|heif|bmp|tiff?)$/.test(nameLower);
    if (!isImage) {
      input.value = "";
      clearMeta();
      this.showError("hinge_photo", "Please choose an image file (JPG, PNG, HEIC, etc.).");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      input.value = "";
      clearMeta();
      this.showError("hinge_photo", "That photo is over 10 MB. Please use a smaller image.");
      return;
    }

    this.hideError("hinge_photo");
    this.hideSubmitError();
    if (nameEl) {
      nameEl.textContent = file.name;
      nameEl.classList.remove("is-hidden");
    }
    if (previewEl && window.URL && typeof window.URL.createObjectURL === "function") {
      previewEl.src = window.URL.createObjectURL(file);
      previewEl.classList.remove("is-hidden");
    }
  };

  DoubleDoorSheet.prototype.clearHingePhoto = function () {
    var input = this.getHingePhotoInput();
    if (input) input.value = "";
    var nameEl = this.container.querySelector("[data-hinge-photo-name]");
    if (nameEl) {
      nameEl.classList.add("is-hidden");
      nameEl.textContent = "";
    }
    var previewEl = this.container.querySelector("[data-hinge-photo-preview]");
    if (previewEl) {
      previewEl.classList.add("is-hidden");
      previewEl.removeAttribute("src");
    }
    this.hideError("hinge_photo");
  };

  // ----- Validation -> cart line properties -----

  DoubleDoorSheet.prototype.validate = function () {
    var state = this.state;
    var props = {};

    if (state.path === "machining") {
      if (!state.mach_type) {
        return { valid: false, message: "Please choose Our Standard or Match my existing doors." };
      }

      var middleMark = null;
      if (state.mach_type === "custom") {
        var topMark = this.validateHingeMark("top_hinge_top", true, 1, 15);
        middleMark = this.validateHingeMark("middle_hinge_top", false, 16, 50);
        var bottomMark = this.validateHingeMark("bottom_hinge_top", true, 51, 80);
        if (!topMark.valid || !middleMark.valid || !bottomMark.valid) {
          return { valid: false, message: "Please correct the hinge measurements." };
        }
        if (!this.hasHingePhoto()) {
          this.showError("hinge_photo", "Please add a photo of one of your hinges.");
          return { valid: false, message: "Please add a photo of one of your hinges." };
        }
      }

      if (!state.mach_hinges) {
        return { valid: false, message: "Please tell us whether to include hinges." };
      }
      if (state.mach_hinges === "yes" && !state.mach_hinge_finish) {
        return { valid: false, message: "Please pick a hinge colour." };
      }

      props["Add-On Path"] = "Double Door Machining";
      props["Machining"] =
        state.mach_type === "custom" ? "Custom (match existing)" : "Standard";
      if (state.mach_type === "custom") {
        props["Top Hinge"] = formatHingeMark(state.top_hinge_top);
        props["Bottom Hinge"] = formatHingeMark(state.bottom_hinge_top);
        if (middleMark && !middleMark.empty) {
          props["Middle Hinge"] = formatHingeMark(state.middle_hinge_top);
        }
      }
      props["Lock Prep"] = NO_LOCK_PREP;
      props["Hinges"] = state.mach_hinges === "yes" ? "Yes" : "No";
      if (state.mach_hinges === "yes") {
        props["Hinge Finish"] =
          state.mach_hinge_finish === "black" ? "Black" : "Satin Nickel";
      }
    } else if (state.path === "frame_set") {
      if (!state.frame_type) return { valid: false, message: "Please select a frame material." };
      if (!state.jamb_size) return { valid: false, message: "Please select a frame depth." };
      if (!state.frame_hinges) {
        return { valid: false, message: "Please tell us whether to include hinges." };
      }
      if (state.frame_hinges === "yes" && !state.frame_hinge_finish) {
        return { valid: false, message: "Please pick a hinge colour." };
      }

      props["Add-On Path"] = "Double Door Frame Set";
      props["Frame Type"] = state.frame_type === "mdf" ? "MDF" : "FJ Pine";
      props["Jamb Size"] = state.jamb_size === "4_5_8" ? '4 5/8"' : '6 5/8"';
      props["Lock Prep"] = NO_LOCK_PREP;
      props["Hinges"] = state.frame_hinges === "yes" ? "Yes" : "No";
      if (state.frame_hinges === "yes") {
        props["Hinge Finish"] =
          state.frame_hinge_finish === "black" ? "Black" : "Satin Nickel";
      }
    } else {
      return { valid: false, message: "Please pick one of the two options above." };
    }

    return { valid: true, message: "", properties: props };
  };

  DoubleDoorSheet.prototype.collectBaseFormProperties = function (formData) {
    var props = {};

    formData.forEach(function (value, key) {
      if (!key.startsWith("properties[")) return;
      var propName = key.replace(/^properties\[/, "").replace(/\]$/, "");
      var propNameLower = String(propName || "").toLowerCase();
      if (INTERNAL_PROPERTY_BLOCKLIST[propName]) return;
      if (/^_door/.test(propNameLower)) return;
      if (
        propNameLower.indexOf("door config id") !== -1 ||
        propNameLower.indexOf("door main item") !== -1 ||
        propNameLower.indexOf("door main multiplier") !== -1 ||
        propNameLower.indexOf("door add-on") !== -1
      ) {
        return;
      }
      if (/decimal/i.test(propName)) return;
      props[propName] = value;
    });

    return props;
  };

  DoubleDoorSheet.prototype.buildCartPayload = function (addonProperties) {
    var formData = new FormData(this.form);
    var variantId = parseNumber(formData.get("id"), 0);
    var quantity = this.getQuantity();
    var sellingPlan = formData.get("selling_plan");
    var configId =
      "door-config-" +
      Date.now().toString(36) +
      "-" +
      Math.random().toString(36).slice(2, 8);

    if (!variantId) {
      return {
        valid: false,
        message: "Please pick your door size above. The size is for one door."
      };
    }

    var baseProperties = this.collectBaseFormProperties(formData);
    var mergedProperties = Object.assign({}, baseProperties, addonProperties, {
      "_Door Config ID": configId
    });

    var items = [
      {
        id: variantId,
        quantity: quantity,
        properties: mergedProperties
      }
    ];

    if (sellingPlan) {
      items[0].selling_plan = sellingPlan;
    }

    var addonKeys = this.collectAddonKeys();
    for (var i = 0; i < addonKeys.length; i++) {
      var addonKey = addonKeys[i];
      var addon = this.addonProducts[addonKey];

      if (!addon || !addon.variant_id) {
        return {
          valid: false,
          message:
            "One of the options is unavailable right now. Please refresh the page or contact us."
        };
      }

      var multiplier = this.getAddonMultiplier(addonKey);
      items.push({
        id: parseNumber(addon.variant_id, 0),
        quantity: quantity * multiplier,
        properties: {
          "_Door Add-On": "Yes",
          "_Door Config ID": configId,
          "_Door Add-On Multiplier": String(multiplier)
        }
      });
    }

    var hingePhotoFile = null;
    if (this.state.path === "machining" && this.state.mach_type === "custom") {
      var photoInput = this.getHingePhotoInput();
      if (photoInput && photoInput.files && photoInput.files[0]) {
        hingePhotoFile = photoInput.files[0];
      }
    }

    return { valid: true, items: items, hingePhotoFile: hingePhotoFile };
  };

  // ----- Cart submission (same endpoints + events as the single builder) -----

  DoubleDoorSheet.prototype.setSubmitLoading = function () {
    if (!this.submitButton) return;
    this.submitButton.classList.add("is-loading");
    this.submitButton.setAttribute("disabled", "disabled");
  };

  DoubleDoorSheet.prototype.resetSubmitButton = function () {
    if (!this.submitButton) return;
    this.submitButton.classList.remove("is-loading");
    if (!this.submitButton.classList.contains("is-success")) {
      this.submitButton.removeAttribute("disabled");
    }
  };

  DoubleDoorSheet.prototype.finishCartAdd = function () {
    document.dispatchEvent(new CustomEvent("theme:cart:refresh", { bubbles: true }));
    document.dispatchEvent(
      new CustomEvent("exos:cart:added", {
        bubbles: true,
        detail: {
          source: "double-door-sheet",
          button: this.submitButton
        }
      })
    );
    this.resetSubmitButton();
    this.resetAll();
  };

  DoubleDoorSheet.prototype.submitCartPayload = function (items) {
    var self = this;
    this.setSubmitLoading();

    var addUrl =
      (window.theme && window.theme.routes && window.theme.routes.cart_add_url) ||
      "/cart/add";
    if (!/\.js$/.test(addUrl)) addUrl += ".js";

    fetch(addUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json"
      },
      body: JSON.stringify({ items: items })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        if (response && response.status) {
          self.showSubmitError(response.description || "Unable to add items to cart.");
          self.resetSubmitButton();
          return;
        }
        self.finishCartAdd();
      })
      .catch(function () {
        self.showSubmitError("Unable to add items to cart. Please try again.");
        self.resetSubmitButton();
      });
  };

  // Custom machining carries a customer photo, which Shopify only accepts as a
  // multipart upload at add-to-cart time. Add the pair line on its own via
  // FormData (photo attached), then the add-on lines via the normal JSON call.
  // If the add-ons fail, roll the pair back so the cart is never partial.
  DoubleDoorSheet.prototype.submitCartPayloadWithPhoto = function (items, photoFile) {
    var self = this;
    this.setSubmitLoading();

    var routes = (window.theme && window.theme.routes) || {};
    var addUrl = routes.cart_add_url || "/cart/add";
    if (!/\.js$/.test(addUrl)) addUrl += ".js";
    var changeUrl = routes.cart_change_url || "/cart/change";
    if (!/\.js$/.test(changeUrl)) changeUrl += ".js";

    var mainItem = items[0];
    var addonItems = items.slice(1);

    var fd = new FormData();
    fd.append("id", mainItem.id);
    fd.append("quantity", mainItem.quantity);
    if (mainItem.selling_plan) fd.append("selling_plan", mainItem.selling_plan);
    var mainProps = mainItem.properties || {};
    Object.keys(mainProps).forEach(function (key) {
      fd.append("properties[" + key + "]", mainProps[key]);
    });
    fd.append("properties[Hinge Photo]", photoFile, photoFile.name);

    function readJson(response) {
      return response.json().then(function (data) {
        return { ok: response.ok, data: data };
      });
    }

    var addonsFailedMessage =
      "Your hinge photo uploaded, but we couldn't add the door options. Please try again.";

    fetch(addUrl, {
      method: "POST",
      headers: { "X-Requested-With": "XMLHttpRequest", Accept: "application/json" },
      body: fd
    })
      .then(readJson)
      .then(function (res) {
        if (!res.ok || (res.data && res.data.status)) {
          self.showSubmitError(
            (res.data && (res.data.description || res.data.message)) ||
              "Unable to add your doors to the cart. Please try again."
          );
          self.resetSubmitButton();
          return;
        }

        var mainKey = res.data && (res.data.key || res.data.id);

        if (!addonItems.length) {
          self.finishCartAdd();
          return;
        }

        fetch(addUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json"
          },
          body: JSON.stringify({ items: addonItems })
        })
          .then(readJson)
          .then(function (res2) {
            if (!res2.ok || (res2.data && res2.data.status)) {
              self.rollbackCartLine(changeUrl, mainKey, function () {
                self.showSubmitError(addonsFailedMessage);
                self.resetSubmitButton();
              });
              return;
            }
            self.finishCartAdd();
          })
          .catch(function () {
            self.rollbackCartLine(changeUrl, mainKey, function () {
              self.showSubmitError(addonsFailedMessage);
              self.resetSubmitButton();
            });
          });
      })
      .catch(function () {
        self.showSubmitError("Unable to add your doors to the cart. Please try again.");
        self.resetSubmitButton();
      });
  };

  DoubleDoorSheet.prototype.rollbackCartLine = function (changeUrl, key, done) {
    if (!key) {
      if (done) done();
      return;
    }

    fetch(changeUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        Accept: "application/json"
      },
      body: JSON.stringify({ id: key, quantity: 0 })
    })
      .then(function () {
        if (done) done();
      })
      .catch(function () {
        if (done) done();
      });
  };

  // ----- Init -----

  function init(scope) {
    (scope || document)
      .querySelectorAll("[data-double-door-sheet]")
      .forEach(function (container) {
        if (container.__doubleDoorSheet) return;
        container.__doubleDoorSheet = new DoubleDoorSheet(container);
      });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener("theme:product-info:loaded", function (event) {
    init(event.target || document);
  });
  document.addEventListener("shopify:section:load", function (event) {
    init(event.target || document);
  });
})();
